import React from "react";

import {
  Box,
  Grid,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@material-ui/core";
import UpdateIcon from "@material-ui/icons/Update";
import NotificationImportantIcon from "@material-ui/icons/NotificationImportant";
import { makeStyles } from "@material-ui/core/styles";

import {
  APP_UPDATE_DELAY_TEXT,
  JS_PREFIX_REGEX,
  SUPPORTED_JS_ACTIONS,
} from "../../utils/constants";
import { useAnnouncements } from "../../utils/useAnnouncements";
import { ConditionalConfirm } from "./ConditionalConfirm";
import { XIcon } from "./iconography";

export const matchSupportedJsAction = (raw) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const candidate = trimmed.replace(JS_PREFIX_REGEX, "");

  // Match against all registered actions and return the first match.
  for (const [type, def] of Object.entries(SUPPORTED_JS_ACTIONS)) {
    const match = candidate.match(def.regex);
    if (!match) continue;
    return { type, args: def.toArgs(match) };
  }

  return null;
};

const parseAnnouncementLink = (link) => {
  if (!link || typeof link !== "string") return { type: "none" };
  const raw = link.trim();
  if (!raw) return { type: "none" };

  const matched = matchSupportedJsAction(raw);
  if (matched) return matched;

  // If the user tried to provide a JS link but it isn't whitelisted, block it.
  if (JS_PREFIX_REGEX.test(raw) || raw.startsWith("window.")) {
    return { type: "invalid" };
  }

  // Default behavior: treat as an href (existing behavior).
  return { type: "href", href: raw };
};

const getJsActionClickHandler =
  ({ type, args }) =>
  (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const handler = SUPPORTED_JS_ACTIONS?.[type]?.handler;
      if (typeof handler !== "function") return;
      handler(args);
    } catch (e) {
      // Intentionally swallow errors: announcement links should never crash the app.
      // eslint-disable-next-line no-console
      console.warn("Failed to run announcement JS action", e);
    }
  };

const renderAnnouncementMessage = (a) => {
  const parsedLink = parseAnnouncementLink(a?.link);

  if (parsedLink.type === "href") {
    return (
      <Link
        href={parsedLink.href}
        target="_blank"
        rel="noopener noreferrer"
        color="textSecondary"
        variant="body2"
        underline="none"
      >
        {a.message}
      </Link>
    );
  }

  if (parsedLink.type !== "none" && parsedLink.type !== "invalid") {
    return (
      <Link
        component="button"
        onClick={getJsActionClickHandler(parsedLink)}
        color="textSecondary"
        variant="body2"
        underline="none"
      >
        {a.message}
      </Link>
    );
  }

  return (
    <Typography component="span" variant="body2" color="textSecondary">
      {a.message}
    </Typography>
  );
};

const useStyles = makeStyles((t) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: t.spacing(1),
    padding: t.spacing(2),
  },
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  messageRow: {
    display: "flex",
    alignItems: "center", // vertically center icon + text
  },
  urgentIcon: {
    marginRight: t.spacing(0.5),
    verticalAlign: "middle", // baseline alignment fallback
  },
}));

const Announcements = () => {
  const {
    visibleAnnouncements,
    showConfirm,
    dismiss,
    handleUpdate,
    confirmClose,
  } = useAnnouncements();
  const classes = useStyles();
  if (!visibleAnnouncements.length) return null;

  return (
    <Grid item xs={12}>
      <Paper>
        <Box className={classes.container}>
          {visibleAnnouncements.map((a) => (
            <Box key={a.id} className={classes.item}>
              {/* flex‐row for icon + text */}
              <Box className={classes.messageRow}>
                <NotificationImportantIcon
                  fontSize="small"
                  color={a.urgency === 1 ? "action" : "error"}
                  className={classes.urgentIcon}
                />
                {renderAnnouncementMessage(a)}
              </Box>

              {a.forceRefresh ? (
                <IconButton size="small" onClick={handleUpdate}>
                  <UpdateIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" onClick={() => dismiss(a.id)}>
                  <XIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        <ConditionalConfirm
          isOpen={showConfirm}
          title="Application Update"
          content={`An update is required. The app will refresh automatically in ${APP_UPDATE_DELAY_TEXT}.`}
          onClose={confirmClose}
          onConfirm={handleUpdate}
          confirm="Update Now"
          cancel="Close"
        />
      </Paper>
    </Grid>
  );
};

export default Announcements;
